<template>
  <ModalFullscreen
    :show="showModal"
    :modal-width="modalWidth"
    :modal-top="modalTop"
    @close="onCloseModal"
  >
    <template #content>
      <div class="flex flex-auto max-w-full">
        <div
          v-if="isDesktopView"
          class="absolute left-0 flex items-center justify-center my-auto transition-all -translate-y-1/2 cursor-pointer select-none rounded-r-alt-xl top-1/2 w-md hover:w-xl bg-btn-primary-base h-xl"
          @click="prevImage"
        >
          <FaIcon
            icon-class="fas fa-chevron-left text-[1rem]"
            classes="text-white"
          />
        </div>
        <div :class="getContainerStyle()">
          <div
            v-if="isDesktopView"
            class="flex flex-col max-h-full my-auto overflow-auto no-scrollbar shrink-0 max-w-[250px]"
          >
            <Image
              v-for="(image, index) in images"
              :id="IMAGE_REF_PREFIX + index"
              :key="image.id"
              :src="image.url"
              :alt="image.altText"
              :title="image.title"
              draggable="false"
              loading="lazy"
              class="hover:scale-[1.02] transition-all"
              :class="getImageFilterStyle(index)"
              quality="80"
              sizes="xs:120px sm:300px"
              @click="state.activeIndex = index"
            />
          </div>
          <div
            v-if="props.images && props.images.length > 0"
            class="flex flex-col flex-auto touch:md:ml-md touch:md:mr-md lg:ml-md lg:mr-md md:mr-0 my-sm h-[60%] md:h-full"
          >
            <Image
              class="m-auto max-h-[95%]"
              :src="props.images[state.activeIndex].url"
              :alt="props.images[state.activeIndex].altText"
              :title="props.images[state.activeIndex].title"
              quality="80"
              sizes="xs:100vw md:940px"
              draggable="false"
              loading="lazy"
            />
            <p class="mx-auto italic font-bold bold text-border-dark">
              {{
                props.images[state.activeIndex].copyright
                  ? `${props.images[state.activeIndex].title} - &copy; ${
                      props.images[state.activeIndex].copyright
                    }`
                  : props.images[state.activeIndex].title
              }}
            </p>
            <p class="mx-auto text-sm italic text-border-dark px-md md:px-0">
              {{ props.images[state.activeIndex].shortDescription }}
            </p>
          </div>
          <div
            v-if="!isDesktopView"
            class="flex mx-auto overflow-auto no-scrollbar"
          >
            <div
              class="flex items-center justify-center cursor-pointer select-none flex-auton bg-btn-primary-base rounded-r-alt-xl"
              @click="prevImage"
            >
              <FaIcon
                icon-class="fas fa-chevron-left"
                classes="text-white text-[1.5rem]"
              />
            </div>
            <div class="flex overflow-auto no-scrollbar">
              <Image
                v-for="(image, index) in images"
                :id="MOBILE_IMAGE_REF_PREFIX + index"
                :key="image.id"
                :src="image.url"
                :alt="image.altText"
                :title="image.title"
                loading="lazy"
                draggable="false"
                :class="getImageFilterStyle(index)"
                @click="state.activeIndex = index"
              />
            </div>
            <div
              class="flex items-center justify-center flex-auto cursor-pointer select-none bg-btn-primary-base rounded-l-alt-xl"
              @click="nextImage"
            >
              <FaIcon
                icon-class="fas fa-chevron-right"
                classes="text-white text-[1.5rem]"
              />
            </div>
          </div>
        </div>
        <div
          v-if="isDesktopView"
          class="absolute right-0 flex items-center justify-center my-auto transition-all -translate-y-1/2 cursor-pointer select-none rounded-l-alt-xl top-1/2 w-md hover:w-xl bg-btn-primary-base h-xl"
          @click="nextImage"
        >
          <FaIcon
            icon-class="fas fa-chevron-right text-white text-[1rem]"
            classes="text-white"
          />
        </div>
      </div>
    </template>
  </ModalFullscreen>
</template>

<script setup lang="ts">
import ModalFullscreen from '@/components/components/core/fullscreen-modal.vue';
import { reactive } from 'vue';
import type { ImageData } from '../basicImage/type';
import FaIcon from '@/components/fa-icon.vue';
import { Image } from '@/complib';

const MOBILE_IMAGE_REF_PREFIX = 'mobile_img_ref_';
const IMAGE_REF_PREFIX = 'img_ref_';

const isDesktopView = useIsDesktopView();

const props = defineProps({
  images: {
    type: Array as PropType<ImageData[]>,
    required: true,
    default: new Array(0),
  },
  activeIndex: {
    type: Number,
    required: false,
    default: 0,
  },
  show: {
    type: Boolean,
    required: true,
    default: false,
  },
  modalTop: {
    type: Number,
    required: false,
    default: 0,
  },
  modalWidth: {
    type: Number,
    required: false,
    default: 0,
  },
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

function onCloseModal() {
  emit('close');
}

function getContainerStyle(): string {
  if (!isDesktopView.value) {
    return 'flex flex-auto flex flex-col px-0 py-sm';
  }
  return 'flex flex-auto px-20 py-16';
}

function getImageFilterStyle(imageIndex: number): string {
  let style = 'self-auto object-contain mx-auto cursor-pointer ';
  if (!isDesktopView.value) {
    style += ' mx-xs  max-h-20 my-auto';
  } else {
    style += 'max-h-36 my-4';
  }
  if (imageIndex === state.activeIndex) {
    return style;
  }
  return style + ' filter brightness-50 grayscale-[50%]';
}

function scrollSlider(toIndex: number): void {
  if (import.meta.client) {
    let element;
    if (!isDesktopView.value) {
      element = document.getElementById(MOBILE_IMAGE_REF_PREFIX + toIndex);
    } else {
      element = document.getElementById(IMAGE_REF_PREFIX + toIndex);
    }
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

type ImageCarouselDefaultState = {
  activeIndex?: number;
};

const state: ImageCarouselDefaultState = reactive({
  activeIndex: 0,
});

const showModal = ref(false);

watch(
  () => showModal.value,
  (newVal) => {
    usePreventBodyScroll(newVal);
  },
);

function prevImage() {
  if (state.activeIndex === 0) {
    state.activeIndex = props.images.length - 1;
  } else {
    state.activeIndex = state.activeIndex - 1;
  }
  scrollSlider(state.activeIndex);
}

function nextImage() {
  if (state.activeIndex === props.images.length - 1) {
    state.activeIndex = 0;
  } else {
    state.activeIndex = state.activeIndex + 1;
  }
  scrollSlider(state.activeIndex);
}

watch(
  () => props.show,
  (show) => {
    showModal.value = show;
  },
);

watch(
  () => props.activeIndex,
  (activeIndex) => {
    state.activeIndex = activeIndex;
    scrollSlider(state.activeIndex);
  },
);

onUnmounted(() => {
  state.activeIndex = undefined;
  usePreventBodyScroll(false);
});
</script>
